import React, { memo, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import ReactTooltip from 'react-tooltip';

import { getAvatarChat, splitReadUnreadMessage } from '../routines';
import { trans } from '../../../localization';
import { EnumUserChatRole, findEnumById } from '../../../constants/enum';
import { usePreventBubblingScroll } from '../hooks';

const LoadingMessage = () => <div className="loading-message" />;

const NewMessagesLine = ({ show = false }) =>
  show && (
    <div className="new-separator">
      <hr className="separator__hr" />
      <div className="separator__text">
        <span>{trans('nex_new_message')}</span>
      </div>
    </div>
  );

const Avatar = ({
  isSameUser,
  isMessageReceived,
  userRole,
  image,
  ...rest
}) => {
  if (!isMessageReceived) {
    return null;
  }
  return !isSameUser ? (
    <img
      src={getAvatarChat(userRole, image)}
      alt="avatar"
      className="avatar"
      {...rest}
    />
  ) : (
    <div className="avatar" />
  );
};

const SeenStatusIcon = ({ seenStatus, messageId }) => (
  <div className="seen-status-wrapper">
    {seenStatus.map((userSeen) => {
      if (
        userSeen.userRole !== EnumUserChatRole.USER.id &&
        userSeen.lastMessageId &&
        userSeen.lastMessageId === messageId
      ) {
        return (
          <div className="seen-status" key={userSeen.userRole}>
            <img src={getAvatarChat(userSeen.userRole)} alt="avatar" />
          </div>
        );
      }
      return null;
    })}
  </div>
);

const DeletedMessage = ({ messageTooltipData }) => (
  <div
    className="chat-content"
    {...messageTooltipData}
    style={{ fontStyle: 'italic' }}
  >
    {trans('nex_removed_message')}
  </div>
);

const TextMessage = ({ messageTooltipData, content, isSendFail }) => (
  <div
    className={`chat-content ${isSendFail ? 'send-fail' : ''}`}
    title={isSendFail ? trans('nex_send_fail') : ''}
    {...messageTooltipData}
  >
    {content}
  </div>
);

const MessageItem = ({ message, prevMessage, seenStatus }) => {
  const {
    id,
    sendTime,
    message: content,
    sender,
    clientMessageId,
    isDeleted,
    isEdited,
  } = message;
  const messageId = id || clientMessageId;
  const isSendFail = messageId < 0;
  const {
    id: senderId, userRole, name, image,
  } = sender;
  const isMessageReceived = userRole !== EnumUserChatRole.USER.id;
  const enumRole = findEnumById(EnumUserChatRole, userRole);
  const isSameUser = prevMessage && prevMessage.sender.id === senderId;
  const tooltipData = {
    'data-tip': true,
    'data-for': `message-${id}`,
  };
  const avatarTooltipData = isMessageReceived ? tooltipData : {};
  const messageTooltipData = isMessageReceived ? {} : tooltipData;
  return (
    <div
      className={`message-item ${
        isMessageReceived ? 'message-received' : 'message-send'
      } ${!isSameUser ? 'group-message-item' : ''}`}
    >
      {!isSameUser && (
        <div
          className="chat-message-title"
          style={isMessageReceived ? { color: enumRole.color } : {}}
        >
          {`${name} (${trans(enumRole.description)})`}
        </div>
      )}
      <div className="content-wrapper">
        <Avatar
          isSameUser={isSameUser}
          isMessageReceived={isMessageReceived}
          userRole={userRole}
          image={image}
          {...avatarTooltipData}
        />
        {isDeleted ? (
          <DeletedMessage messageTooltipData={messageTooltipData} />
        ) : (
          <TextMessage
            messageTooltipData={messageTooltipData}
            content={content}
            isSendFail={isSendFail}
          />
        )}
        {isEdited && (
          <i
            className="fas fa-pen edit-icon"
            title={trans('nex_edit_message')}
          />
        )}
      </div>
      <ReactTooltip
        id={`message-${id}`}
        effect="solid"
        place="right"
        className="z-index-4"
      >
        <span>{sendTime}</span>
      </ReactTooltip>

      <SeenStatusIcon seenStatus={seenStatus} messageId={messageId} />
    </div>
  );
};

const MessageList = memo(
  ({
    appType,
    messageListRef,
    scrollNewMessageRef,
    scrollBottomRef,
    hasMore,
    isLoading,
    messages,
    hasUnreadMessage,
    userSeenStatus,
    handleGetMessageList,
  }) => {
    const oldestMessageId = messages[0] ? messages[0].id : undefined;
    const { readMessages, unreadMessages, otherSeenStatus } = useMemo(
      () => splitReadUnreadMessage(appType, userSeenStatus, messages),
      [appType, userSeenStatus, messages],
    );
    const { handleScroll } = usePreventBubblingScroll(messageListRef);
    console.log(
      'hasUnreadMessage, unreadMessages',
      hasUnreadMessage,
      unreadMessages,
    );
    return (
      <div className="message-history-list-wrapper">
        <div
          className="message-history-list"
          onWheel={e => handleScroll(e)}
          ref={messageListRef}
        >
          <InfiniteScroll
            isReverse
            loadMore={() => !isLoading && handleGetMessageList(oldestMessageId)}
            hasMore={hasMore}
            loader={<LoadingMessage key={0} />}
            useWindow={false}
            initialLoad={false}
          >
            {readMessages.map((message, index) => (
              <MessageItem
                key={message.id || message.clientMessageId}
                message={message}
                prevMessage={readMessages[index - 1]}
                seenStatus={otherSeenStatus}
              />
            ))}

            <NewMessagesLine
              show={hasUnreadMessage && unreadMessages.length > 0}
            />
            <div
              style={{ float: 'left', clear: 'both' }}
              ref={scrollNewMessageRef}
            />

            {unreadMessages.map((message, index) => (
              <MessageItem
                key={message.id || message.clientMessageId}
                message={message}
                prevMessage={unreadMessages[index - 1]}
                seenStatus={otherSeenStatus}
              />
            ))}
            <div
              style={{ float: 'left', clear: 'both' }}
              ref={scrollBottomRef}
            />
          </InfiniteScroll>
        </div>
      </div>
    );
  },
);

export default MessageList;



// WEBPACK FOOTER //
// ./foody/src/pages/ChatBox/ChatChannel/MessageList.jsx